import { inject } from '@angular/core';
import { ToolsHelperService } from '../cross-team-shared/misc-services/tools-helper.service';
import { settingsStoreToken } from '../cross-team-shared/state/setting-state';
import { SettingsKeyboardShortcutsService } from '../outputs/annotation/annotation-shared/side-panel/settings-keyboard-shortcuts.service';
import { AnnotationListService } from '../outputs/annotation/annotation-shared/toolbars/shape-list-and-tag-editor/annotation-list/annotation-list.service';
import { AvailableAttributeService } from '../outputs/annotation/annotation-shared/utils/available-attribute.service';
import { DefaultAttributeService } from '../outputs/annotation/annotation-shared/utils/default-attribute.service';
import { EventTrackingService } from '../shared/event-tracking/event-tracking.service';
import { RootTrackerWrapper } from '../shared/event-tracking/root-tracker-wrapper';
import { DraftSubmissionResource } from '../shared/resources/draft-submission-resource.service';
import { DebounceSaveManagerService } from '../shared/save-manager/debounce-save-manager.service';
import { PeriodicSaveManagerService } from '../shared/save-manager/periodic-save-manager.service';
import { CVFeedbackPopoverService } from './feedback/services/cv-feedback-popover.service';
import { FeedbackPersistenceService } from './feedback/services/feedback-persistence.service';
import { FeedbackResource } from './feedback/services/feedback-resource.service';
import { FieldFeedbackService } from './feedback/services/field-feedback.service';
import { CustomJsService } from './h3ml/custom-js.service';
import { SplitViewInitializationService } from './h3ml/dsl/sensor-fusion/split-view/split-view-initialization.service';
import { SplitViewService } from './h3ml/dsl/sensor-fusion/split-view/split-view.service';
import { SettingsService } from './h3ml/dsl/sensor-fusion/view-settings/settings.service';
import { H3mlHelper } from './h3ml/h3ml-helper';
import { TaskWorkspaceStore } from './h3ml/state/task-workspace-store';
import { AnnotationLoadingService } from './http/loading/annotation-loading.service';
import { ClientFeedbackLoadingService } from './http/loading/client-feedback-loading.service';
import { FeatureFlagLoadingService } from './http/loading/feature-flag-loading.service';
import { FeedbackLoadingService } from './http/loading/feedback-loading.service';
import { LastRoundFeedbackLoadingService } from './http/loading/last-round-feedback-loading-service';
import { LutLoadingService } from './http/loading/lut-loading.service';
import { ProjectLoadingService } from './http/loading/project-loading.service';
import { SampleLoadingService } from './http/loading/sample-loading.service';
import { SampleTaskLoadingService } from './http/loading/sample-task-loading.service';
import { SettingsLoadingService } from './http/loading/settings-loading.service';
import { SubmissionLoadingService } from './http/loading/submission-loading.service';
import { TaskLoadingService } from './http/loading/task-loading.service';
import { TemplateLoadingService } from './http/loading/template-loading.service';
import { AnnotationChangeListenerService } from './http/persistence/draft/annotation-change-listener.service';
import { ObjectChangeOperationService } from './http/persistence/draft/object-change-operation.service';
import { ShapeChangeOperationService } from './http/persistence/draft/shape-change-operation.service';
import { TaskChangeOperationService } from './http/persistence/draft/task-change-operation.service';
import { FusionManagerFactory } from './outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { CVToolbarKeyboardShortcutService } from './outputs/annotation/annotation-shared/toolbars/static-toolbar/cv-toolbar-keyboard-shortcut.service';
import { FocusedObjectService } from './outputs/annotation/annotation-shared/utils/focused-object.service';
import { ObjectSelectionService } from './outputs/annotation/annotation-shared/utils/object-selection.service';
import { RootFocusedAnnotationService } from './outputs/annotation/annotation-shared/utils/root-focused-annotation.service';
import { RootFocusedShapeService } from './outputs/annotation/annotation-shared/utils/root-focused-shape.service';
import { RootPushAndTrackService } from './outputs/annotation/annotation-shared/utils/root-push-and-track.service';
import { RootShapeSelectionService } from './outputs/annotation/annotation-shared/utils/root-shape-selection.service';
import { IdleTimeoutService } from './production-workspace/idle-timer/idle-timeout.service';
import { StopWorkService } from './production-workspace/stop-work-button/stop-work.service';
import { AutoQaService } from './production-workspace/submissions/auto-qa.service';
import { DraftSubmissionService } from './production-workspace/submissions/draft-submission.service';
import { PersistenceManager } from './production-workspace/submissions/persistence-manager';
import { RequiredAnnotationsService } from './production-workspace/submissions/required-annotations.service';
import { SubmissionLoggingService } from './production-workspace/submissions/submission-logging.service';
import { SubmitTaskService } from './production-workspace/submissions/submit-task.service';
import { SubmitTaskButtonService } from './production-workspace/submit-task-button/submit-task-button.service';
import { TaskPausingService } from './production-workspace/task-pausing/task-pausing.service';
import { TimerService } from './production-workspace/timer-badge/timer.service';
import { SampleTaskNavigationService } from './sampling-workspace/services/sample-task-navigation.service';
import { TaskReviewFinalizeResourceService } from './sampling-workspace/services/task-review-finalize-resource.service';
import { SamplingWorkspaceStore } from './sampling-workspace/state/sampling-workspace-store';
import { FeedbackListService } from './shared/feedback/feedback-list.service';
import { EscapeKeyboardShortcutService } from './shared/keyboard-shortcuts/escape-keyboard-shortcut.service';
import { RootKeyboardShortcutService } from './shared/keyboard-shortcuts/root-keyboard-shortcut.service';
import { AnnotationColorUpdateService } from './shared/misc-services/annotation-color-update.service';
import { AnnotationColorService } from './shared/misc-services/annotation-color.service';
import { AnnotationCreateDeleteService } from './shared/misc-services/annotation-create-delete.service';
import { AnnotationFilterUpdateService } from './shared/misc-services/annotation-filter-update.service';
import { AnnotationFilteringService } from './shared/misc-services/annotation-filtering.service';
import { AnnotationTransientInfoService } from './shared/misc-services/annotation-transient-info.service';
import { FeatureFlagService } from './shared/misc-services/feature-flag.service';
import { ObjectClassService } from './shared/misc-services/object-class.service';
import { ObjectScopingService } from './shared/misc-services/object-scoping.service';
import { ObjectSortingService } from './shared/misc-services/object-sorting.service';
import { ObjectVisibilityService } from './shared/misc-services/object-visibility.service';
import { ObjectService } from './shared/misc-services/object.service';
import { ObjectlessShapeService } from './shared/misc-services/objectless-shape.service';
import { PersistenceService } from './shared/misc-services/persistence.service';
import { PointCloudToVectorShapeService } from './shared/misc-services/point-cloud-to-vector-shape.service';
import { RootUndoService } from './shared/misc-services/root-undo.service';
import { ShapeService } from './shared/misc-services/shape.service';
import { SmartLoaderService } from './shared/misc-services/smart-loader.service';
import { TaxonomyLoadingService } from './shared/misc-services/taxonomy-loading.service';
import { TrainingTaskServiceObjectless } from './shared/misc-services/training-task-objectless.service';
import { TrainingTaskService } from './shared/misc-services/training-task.service';
import { TrainingTemplateResource } from './shared/misc-services/training-template-resource.service';
import { TrainingTemplateService } from './shared/misc-services/training-template.service';
import { UndoRedoKeyboardShortcutService } from './shared/misc-services/undo-redo-keyboard-shortcut.service';
import { AnnotationResource } from './shared/resources/annotation-resource.service';
import { ClientFeedbackResource } from './shared/resources/client-issue-resource.service';
import { TrainingTaskResourceService } from './shared/resources/training-task-resource.service';
import { ViewContainerMappingService } from './shared/view-container-mapping/view-container-mapping.service';

export const workspaceProviders = [
  // stores
  TaskWorkspaceStore,
  FusionManagerFactory,
  {
    provide: settingsStoreToken,
    useFactory: () => inject(TaskWorkspaceStore)('settings'),
  },
  // loading
  AnnotationLoadingService,
  ClientFeedbackLoadingService,
  FeatureFlagLoadingService,
  FeedbackLoadingService,
  SampleLoadingService,
  SampleTaskLoadingService,
  TaskReviewFinalizeResourceService,
  LutLoadingService,
  ProjectLoadingService,
  TaskLoadingService,
  TemplateLoadingService,
  SettingsLoadingService,
  // injects task workspace store
  AnnotationListService,
  ToolsHelperService,
  SettingsService,
  RequiredAnnotationsService,
  TimerService,
  AnnotationLoadingService,
  LastRoundFeedbackLoadingService,
  TemplateLoadingService,
  ClientFeedbackLoadingService,
  ProjectLoadingService,
  AnnotationResource,
  TrainingTaskResourceService,
  ShapeService,
  ObjectlessShapeService,
  AnnotationTransientInfoService,
  ObjectScopingService,
  TrainingTaskService,
  ObjectSortingService,
  TrainingTemplateService,
  TrainingTaskServiceObjectless,
  AnnotationFilteringService,
  AnnotationColorService,
  AnnotationCreateDeleteService,
  TaxonomyLoadingService,
  ObjectSelectionService,
  ObjectVisibilityService,
  RootShapeSelectionService,
  RootFocusedAnnotationService,
  FocusedObjectService,
  DefaultAttributeService,
  SamplingWorkspaceStore,
  EventTrackingService,
  RootTrackerWrapper,
  DraftSubmissionResource,
  DraftSubmissionService,
  FeatureFlagService,
  SubmitTaskService,
  SubmissionLoggingService,
  AutoQaService,
  CustomJsService,
  PersistenceService,
  PersistenceManager,
  AnnotationChangeListenerService,
  RootUndoService,
  ShapeChangeOperationService,
  ObjectChangeOperationService,
  TaskChangeOperationService,
  H3mlHelper,
  FieldFeedbackService,
  SmartLoaderService,
  RootKeyboardShortcutService,
  EscapeKeyboardShortcutService,
  SettingsKeyboardShortcutsService,
  ObjectService,
  RootPushAndTrackService,
  ObjectClassService,
  AvailableAttributeService,
  ClientFeedbackResource,
  AnnotationFilterUpdateService,
  AnnotationColorUpdateService,
  IdleTimeoutService,
  TaskPausingService,
  TrainingTemplateResource,
  RootFocusedShapeService,
  CVToolbarKeyboardShortcutService,
  CVFeedbackPopoverService,
  SubmitTaskButtonService,
  StopWorkService,
  SubmissionLoadingService,
  PointCloudToVectorShapeService,
  SampleTaskNavigationService,
  UndoRedoKeyboardShortcutService,
  FeedbackPersistenceService,
  FeedbackResource,
  FeedbackListService,
  // initialized in the workspace
  PeriodicSaveManagerService,
  DebounceSaveManagerService,
  // is stateful and linked to workspace
  ViewContainerMappingService,
  SplitViewInitializationService,
  SplitViewService,
];
